import React, { useState } from 'react';
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { scrollY } = useScroll();
  const navigate = useNavigate();
  const location = useLocation();

  useMotionValueEvent(scrollY, 'change', (latest) => {
    setIsScrolled(latest > 50);
  });

  const navLinks = ['Home', 'Services', 'Solutions', 'About', 'Contact'];

  const handleNavigation = (e, link) => {
    e.preventDefault();
    if (link === 'About') {
      navigate('/about');
      window.scrollTo(0, 0);
    } else if (link === 'Contact') {
      navigate('/contact');
      window.scrollTo(0, 0);
    } else if (link === 'Services') {
      navigate('/services');
      window.scrollTo(0, 0);
    } else if (link === 'Solutions') {
      navigate('/solutions');
      window.scrollTo(0, 0);
    } else if (link === 'Home') {
      if (location.pathname !== '/') {
        navigate('/');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const targetHash = `#${link.toLowerCase()}`;
      if (location.pathname !== '/') {
        navigate(`/${targetHash}`);
      } else {
        const element = document.getElementById(link.toLowerCase());
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.location.hash = targetHash;
        }
      }
    }
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 px-4">
        <motion.div
          animate={{ scale: isScrolled ? 0.97 : 1 }}
          transition={{ type: 'spring', stiffness: 100, damping: 20, duration: 0.6, ease: 'easeInOut' }}
          className={`max-w-6xl mx-auto mt-4 px-4 border border-white/10 rounded-full ${
            isScrolled
              ? 'py-2 bg-slate-900/90 backdrop-blur-2xl shadow-xl shadow-black/20'
              : 'py-5 bg-slate-900/30 backdrop-blur-md shadow-2xl shadow-black/30'
          }`}
        >
          <div className="relative flex items-center justify-between">
            {/* Logo */}
            <motion.button
              onClick={(e) => handleNavigation(e, 'Home')}
              whileHover={{ scale: 1.05 }}
              transition={{ type: 'spring', stiffness: 100, damping: 20, duration: 0.6, ease: 'easeInOut' }}
              className="inline-flex items-center rounded-full bg-white border border-white/40 px-3 py-1.5 shadow-lg shadow-[#EFCD86]/10 hover:shadow-[#EFCD86]/20 transition-all duration-500 z-50"
            >
              <motion.img
                src="/assest/logo/logo.png"
                alt="MakSoft logo"
                animate={{
                  width: isScrolled ? 40 : 48,
                  height: isScrolled ? 40 : 48,
                }}
                transition={{ type: 'spring', stiffness: 100, damping: 20, duration: 0.6, ease: 'easeInOut' }}
                className="object-contain brightness-110 contrast-110"
              />
            </motion.button>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-2">
              {navLinks.map((link) => (
                <motion.a
                  key={link}
                  href={link === 'About' ? '/about' : link === 'Services' ? '/services' : link === 'Solutions' ? '/solutions' : link === 'Home' ? '/' : `/#${link.toLowerCase()}`}
                  onClick={(e) => handleNavigation(e, link)}
                  onMouseEnter={() => setHoveredLink(link)}
                  onMouseLeave={() => setHoveredLink(null)}
                  whileHover={{
                    x: link.charCodeAt(0) % 2 === 0 ? 2 : -2,
                    y: link.charCodeAt(0) % 3 === 0 ? -2 : 2,
                  }}
                  transition={{ type: 'spring', stiffness: 100, damping: 20, duration: 0.6, ease: 'easeInOut' }}
                  className="relative px-3 py-1 text-sm font-medium rounded-full transition-colors duration-500 text-gray-300 hover:text-[#EFCD86] hover:bg-[#EFCD86]/10"
                >
                  {hoveredLink === link && (
                    <motion.span
                      layoutId="active-nav-indicator"
                      className="absolute left-3 right-3 -bottom-1 h-[2px] rounded-full bg-[#EFCD86]"
                      transition={{ type: 'spring', stiffness: 100, damping: 20, duration: 0.5, ease: 'easeInOut' }}
                    />
                  )}
                  {link}
                </motion.a>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <motion.button
                onClick={() => {
                  navigate('/contact');
                  window.scrollTo(0, 0);
                }}
                whileHover={{ scale: 1.05 }}
                transition={{ type: 'spring', stiffness: 100, damping: 20, duration: 0.6, ease: 'easeInOut' }}
                className="px-5 py-2 rounded-full border-2 border-[#EFCD86] text-[#EFCD86] bg-transparent hover:bg-[#EFCD86]/10 transition-transform duration-500 text-sm font-medium"
              >
                Contact Us
              </motion.button>
              <motion.button
                onClick={(e) => handleNavigation(e, 'Pricing')}
                whileHover={{ scale: 1.05 }}
                transition={{ type: 'spring', stiffness: 100, damping: 20, duration: 0.6, ease: 'easeInOut' }}
                className="px-5 py-2 rounded-full bg-gradient-to-r from-[#046A8C] to-[#67B253] text-white font-semibold hover:shadow-lg hover:shadow-[#046A8C]/30 transition-transform duration-500 text-sm"
              >
                Get Quote
              </motion.button>
            </div>

            {/* Mobile Hamburger */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 100, damping: 20, duration: 0.6, ease: 'easeInOut' }}
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10 border border-white/20 text-white backdrop-blur-md"
              aria-label="Open mobile menu"
            >
              <Menu size={20} />
            </motion.button>

            {/* Gradient border effect */}
            <div className="absolute left-10 right-10 bottom-0 h-[1px] bg-gradient-to-r from-transparent via-[#EFCD86] to-transparent opacity-50" />
          </div>
        </motion.div>
      </nav>

      {/* Mobile Floating Menu Card */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: 'easeInOut' }}
              className="fixed inset-0 top-16 mt-16 bg-black/30 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 100, damping: 20, duration: 0.6, ease: 'easeInOut' }}
              className="fixed inset-0 top-16 mt-16 bottom-auto left-4 right-4 z-40 rounded-2xl bg-slate-900/85 border border-white/15 backdrop-blur-2xl shadow-2xl shadow-black/30 p-4 lg:hidden"
            >
              <div className="absolute top-4 right-4">
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
                  aria-label="Close mobile menu"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex flex-col gap-2 mt-10">
                {navLinks.map((link) => (
                  <motion.a
                    key={link}
                    href={link === 'About' ? '/about' : link === 'Services' ? '/services' : link === 'Solutions' ? '/solutions' : link === 'Home' ? '/' : `/#${link.toLowerCase()}`}
                    onClick={(e) => {
                      setIsMobileMenuOpen(false);
                      handleNavigation(e, link);
                    }}
                    whileHover={{ x: 2 }}
                    transition={{ type: 'spring', stiffness: 100, damping: 20, duration: 0.6, ease: 'easeInOut' }}
                    className="px-4 py-2.5 rounded-xl text-sm font-medium transition-colors duration-500 text-gray-200 hover:text-[#EFCD86] hover:bg-white/10"
                  >
                    {link}
                  </motion.a>
                ))}
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button 
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate('/contact');
                      window.scrollTo(0, 0);
                    }}
                    className="px-4 py-2.5 rounded-full border-2 border-[#EFCD86] text-[#EFCD86] bg-transparent hover:bg-[#EFCD86]/10 transition-colors duration-500 text-sm font-medium"
                  >
                    Contact Us
                  </button>
                  <button 
                    onClick={(e) => {
                      setIsMobileMenuOpen(false);
                      handleNavigation(e, 'Pricing');
                    }}
                    className="px-4 py-2.5 rounded-full bg-gradient-to-r from-[#046A8C] to-[#67B253] text-white font-semibold hover:shadow-lg hover:shadow-[#046A8C]/30 transition-all duration-500 text-sm"
                  >
                    Get Quote
                  </button>
                </div>
              </div>
              <div className="mt-3 h-[1px] bg-gradient-to-r from-transparent via-[#EFCD86] to-transparent opacity-50" />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
